import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('powertrack_token'));
    const [loading, setLoading] = useState(true);

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // Add token to requests
    api.interceptors.request.use((config) => {
        const currentToken = localStorage.getItem('powertrack_token');
        if (currentToken) {
            config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
    });

    // Handle 401/403 responses
    api.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401) {
                logout();
            }
            return Promise.reject(error);
        }
    );

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            logout();
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password, companyCode) => {
        const response = await api.post('/auth/login', { 
            email, 
            password, 
            company_code: companyCode 
        });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('powertrack_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const register = async (email, password, name, companyCode) => {
        const response = await api.post('/auth/register', { 
            email, 
            password, 
            name, 
            company_code: companyCode 
        });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('powertrack_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('powertrack_token');
        setToken(null);
        setUser(null);
    };

    const isMaster = user?.role === 'master';
    const isAdmin = user?.role === 'admin' || user?.role === 'master';

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        api,
        isAuthenticated: !!user,
        isMaster,
        isAdmin,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
